import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ShieldAlert, TrendingDown, Lightbulb, ShieldCheck } from 'lucide-react';
import { api } from '@/lib/api';
import { colors, getNeumorphicShadow } from '@/lib/designSystem';
import { useTheme } from '@/contexts/ThemeContext';

const IntelligenceSummaryStrip = ({ dataQuality, refreshKey = 0 }) => {
  const { theme } = useTheme();
  const currentColors = colors[theme];
  const [driftOpen, setDriftOpen] = useState(0);
  const [newInsights, setNewInsights] = useState(0);

  useEffect(() => {
    Promise.allSettled([
      api.getPriceDriftAlerts({ limit: 1 }),
      api.getInsightsSummary(),
    ]).then(([driftRes, insightsRes]) => {
      if (driftRes.status === 'fulfilled') {
        setDriftOpen(driftRes.value.data?.summary?.by_status?.OPEN || 0);
      }
      if (insightsRes.status === 'fulfilled') {
        setNewInsights(insightsRes.value.data?.new || 0);
      }
    });
  }, [refreshKey]);

  const criticalCount = dataQuality?.summary?.by_severity?.critical || 0;
  const cov = dataQuality?.coverage;
  const coverageScore = cov
    ? Math.round(
        ((cov.mapping_status?.mapped || 0) +
          (cov.sku_size_present || 0) +
          (cov.unit_norm_present || 0)) /
          3 *
          100
      )
    : 0;

  const metrics = [
    { label: 'Critical Issues', value: criticalCount, icon: ShieldAlert, color: currentColors.accentRed, testId: 'kpi-critical' },
    { label: 'Open Price Drifts', value: driftOpen, icon: TrendingDown, color: currentColors.accentOrange, testId: 'kpi-drifts' },
    { label: 'New Insights', value: newInsights, icon: Lightbulb, color: currentColors.accentBlue, testId: 'kpi-insights' },
    { label: 'Data Health', value: `${coverageScore}%`, icon: ShieldCheck, color: currentColors.accentGreen, testId: 'kpi-coverage' },
  ];

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="intelligence-summary-strip">
      {metrics.map((m, i) => (
        <motion.div
          key={m.label}
          className="rounded-xl p-4"
          style={{ background: currentColors.cardBg, boxShadow: getNeumorphicShadow(theme) }}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: i * 0.08 }}
          data-testid={m.testId}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ background: m.color + '20' }}
            >
              <m.icon size={20} style={{ color: m.color }} />
            </div>
            <div>
              <p className="text-xl font-bold" style={{ color: currentColors.textPrimary }}>
                {m.value}
              </p>
              <p className="text-xs" style={{ color: currentColors.textSecondary }}>
                {m.label}
              </p>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default IntelligenceSummaryStrip;
